import { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { getAttemptResult } from "../../services/Attempts/AttemptService";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import { Button } from "../../components/forms/Buttons/Button";
import { isCanceledError, resolveExamErrorText } from "../../utils/examError";
import { BackLink } from "./parts";

function formatDuration(seconds, t) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return t("exam_taking.duration_value", { minutes: m, seconds: s });
}

export default function ExamResult() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // My attempts opens a result with the state {from: "/my-attempts"}: the way back then leads there; from the
  // finished exam it leads to the exams.
  const back =
    location.state?.from === "/my-attempts"
      ? { to: "/my-attempts", label: t("nav.my_attempts") }
      : { to: "/available-exams", label: t("exam_taking.back_to_exams") };

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState(null);
  // 403 results_hidden: the exam publishes results later (show_results_immediately is off).
  const [resultsHidden, setResultsHidden] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    getAttemptResult({ uuid, signal: controller.signal })
      .then((data) => {
        if (!isCurrent) return;
        setResult(data);
      })
      .catch((error) => {
        if (!isCurrent || isCanceledError(error)) return;
        if (error?.response?.data?.code === "results_hidden") {
          setResultsHidden(true);
          return;
        }
        setErrorText(
          resolveExamErrorText(t, error, "exam_taking.failed_to_load_result")
        );
      })
      .finally(() => {
        if (!isCurrent) return;
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [uuid, t]);

  if (loading) return <SimpleLoader className="h-8 w-8" />;
  if (resultsHidden) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm motion-safe:animate-fade-in">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/50">
          <CheckCircleIcon className="h-8 w-8 text-emerald-500" aria-hidden="true" />
        </span>
        <h2 className="mb-2 text-lg font-semibold tracking-tight text-slate-900">
          {t("exam_taking.results_hidden_title")}
        </h2>
        <p className="text-sm leading-6 text-slate-500">{t("exam_taking.results_hidden_description")}</p>
        <Button
          text={back.label}
          color="secondary"
          onClick={() => navigate(back.to)}
          className="mt-6 max-sm:h-11"
        />
      </div>
    );
  }
  if (errorText) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
        <p className="text-sm text-slate-600">{errorText}</p>
        <Button
          text={back.label}
          color="secondary"
          onClick={() => navigate(back.to)}
          className="mt-4 max-sm:h-11"
        />
      </div>
    );
  }

  const passed = result.passed;
  const score = parseFloat(result.score_percentage).toFixed(1);
  const hasTests = result.test_results?.length > 0;
  // The server says when a review would be refused (results hidden, attempts left).
  const canReview = result.review_available !== false;
  // The table's number columns stay narrow on phones, so the test's name keeps its room.
  const numberCell = "px-2 py-3.5 text-center text-sm tabular-nums text-slate-700 sm:px-4";

  return (
    <div className="mx-auto w-full max-w-3xl motion-safe:animate-fade-in">
      {/* The way back sits where the exam's page has it; the card ends with its one action. */}
      <BackLink to={back.to} className="mb-4">
        {back.label}
      </BackLink>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div
          className={`border-b px-6 py-8 text-center ${
            passed
              ? "border-emerald-100 bg-gradient-to-b from-emerald-50/80 to-white"
              : "border-red-100 bg-gradient-to-b from-red-50/80 to-white"
          }`}
        >
          {/* Which exam the result is of (the server sends it since 2026-10-27; an older one does not). */}
          {result.exam?.name && (
            <h1 className="mb-5 text-lg font-semibold tracking-tight text-slate-900 [overflow-wrap:anywhere]">{result.exam.name}</h1>
          )}
          <div
            className={`mb-4 inline-flex h-32 w-32 items-center justify-center rounded-full bg-white shadow-sm ring-8 ${
              passed ? "ring-emerald-500/20" : "ring-red-500/20"
            }`}
          >
            <span
              className={`text-3xl font-bold tabular-nums tracking-tight ${
                passed ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {score}%
            </span>
          </div>
          <div className="flex items-center justify-center">
            {passed ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 ring-1 ring-inset ring-emerald-600/20">
                <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                <span className="text-base font-semibold text-emerald-700">
                  {t("exam_taking.passed")}
                </span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 ring-1 ring-inset ring-red-600/20">
                <XCircleIcon className="h-5 w-5 text-red-500" />
                <span className="text-base font-semibold text-red-700">
                  {t("exam_taking.failed")}
                </span>
              </span>
            )}
          </div>
        </div>

        <div className="p-5 sm:p-8">
          <div className={`grid grid-cols-2 gap-3 md:grid-cols-3 ${hasTests || canReview ? "mb-8" : ""}`}>
            <StatCard
              label={t("exam_taking.total_questions")}
              value={result.total_questions}
            />
            <StatCard
              label={t("exam_taking.answered")}
              value={result.answered_questions}
            />
            <StatCard
              label={t("exam_taking.correct_answers")}
              value={result.correct_answers}
              color="text-emerald-600"
            />
            <StatCard
              label={t("exam_taking.incorrect_answers")}
              value={result.incorrect_answers}
              color="text-red-600"
            />
            <StatCard
              label={t("exam_taking.unanswered")}
              value={result.unanswered_questions}
              color="text-slate-500"
            />
            <StatCard
              label={t("exam_taking.time_taken")}
              value={formatDuration(result.time_taken_seconds, t)}
            />
          </div>

          {hasTests && (
            <div className={canReview ? "mb-8" : ""}>
              <h3 className="mb-3 text-base font-semibold text-slate-900">
                {t("exam_taking.test_breakdown")}
              </h3>
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 sm:px-6">
                        {t("exam_taking.test")}
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500 sm:px-4">
                        {t("exam_taking.correct")}
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500 sm:px-4">
                        {t("exam_taking.total")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 sm:px-6">
                        {t("exam_taking.score")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.test_results.map((tr, i) => (
                      <tr key={i} className="transition-colors duration-150 hover:bg-slate-50/80">
                        <td className="px-4 py-3.5 text-sm font-medium text-slate-900 sm:px-6">
                          {tr.test_name}
                        </td>
                        <td className={numberCell}>
                          {tr.correct}
                        </td>
                        <td className={numberCell}>
                          {tr.total_questions}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm font-semibold tabular-nums text-slate-900 sm:px-6">
                          {parseFloat(tr.score_pct).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {canReview && (
            <div className="border-t border-slate-100 pt-6">
              <Button
                text={t("exam_taking.review")}
                color="primary"
                onClick={() => navigate(`/exam/attempt/${uuid}/review`, { state: location.state })}
                className="max-sm:!h-11 max-sm:w-full"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// A count of the result; a zero is muted, never green or red.
function StatCard({ label, value, color = "text-slate-900" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-4 text-center">
      <p className="mb-1 text-xs font-medium text-slate-500">{label}</p>
      <p className={`text-2xl font-semibold tabular-nums tracking-tight ${value === 0 ? "text-slate-400" : color}`}>{value}</p>
    </div>
  );
}
